import {useConfirmDeletePatientPrescription} from '@/modules/patients/api/use-delete-patient-prescription'
import {openAddParameterDialog} from '@/modules/patients/components/patient-details/therapy/dialogs/util'
import {useTherapyFlatRows} from '@/modules/patients/hooks/use-therapy-flat-rows'
import {usePatientTherapyContext} from '@/shared/contexts'
import {useCan} from '@/shared/hooks/use-permissions'
import {Box, Button, Checkbox, Table, Text} from '@chakra-ui/react'
import {PatientProtocol, PatientTherapy} from '@cypro/engine/queries'
import {useTranslate} from '@tolgee/react'
import {memo, MouseEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {LuMoveDown, LuMoveUp, LuTextSearch} from 'react-icons/lu'
import {TableVirtuoso, TableVirtuosoHandle} from 'react-virtuoso'
import {FlatRow, TherapyRowVirtuoso} from './rows'
import {openPatientTherapySequenceDrawer} from '@/modules/patients-therapies/components/patient-therapy-sequence-drawer/util'
import {getDayRowIds, getGroupSelectionState, getSelectableRowIds} from '@/modules/patients/therapy-selection'

export function TherapyTableNew({scrollableRef}: {scrollableRef: HTMLElement}) {
  const {patientProtocols, isPendingHl7Import} = usePatientTherapyContext()
  const flatRows = useTherapyFlatRows()

  return (
    <TherapyTableWithVirtuoso
      flatRows={flatRows}
      scrollableRef={scrollableRef}
      patientProtocols={patientProtocols}
      readOnly={isPendingHl7Import}
    />
  )
}

const rowStyle: React.CSSProperties = {
  borderBottom: '1px solid #d4d4d8',
  maxHeight: '3rem',
}

const therapyRowHover = {
  bg: 'gray.100',
  cursor: 'pointer',
}

function getRowTherapyId(item: FlatRow | undefined): string | undefined {
  switch (item?.type) {
    case 'therapy':
    case 'note':
      return item.therapy.id
    case 'medicine-note':
      return item.therapyId
    default:
      return undefined
  }
}

function isRowSelected(item: FlatRow | undefined, selectedIds: ReadonlySet<string>) {
  const therapyId = getRowTherapyId(item)
  return therapyId != null && selectedIds.has(therapyId)
}

function VirtuosoTableRow(props: Table.RowProps & {item?: FlatRow}) {
  const {item, ...rest} = props
  const {selectedTherapyIds} = usePatientTherapyContext()
  const isClickable = item?.type === 'therapy'

  return (
    <Table.Row
      {...rest}
      style={rowStyle}
      bg={isRowSelected(item, selectedTherapyIds) ? 'cyan.subtle' : undefined}
      _hover={isClickable ? therapyRowHover : undefined}
    />
  )
}

const virtuosoTableComponents = {
  Table: (props: Table.RootProps) => (
    <Table.Root {...props} tableLayout={'fixed'}>
      <colgroup>
        <col style={{width: '2rem'}} />
        <col style={{width: '1rem'}} />
        <col style={{width: '7rem'}} />
        <col style={{width: '14rem'}} />
        <col style={{minWidth: '15rem'}} />
        <col style={{minWidth: '4rem'}} />
        <col style={{minWidth: '5rem'}} />
        <col style={{width: '10rem'}} />
        <col style={{width: '10rem'}} />
        <col style={{width: '10rem'}} />
        <col style={{width: '5rem'}} />
      </colgroup>
      {props.children}
    </Table.Root>
  ),
  TableHead: (props: Table.HeaderProps) => <Table.Header {...props} />,
  TableRow: VirtuosoTableRow,
}

export const TherapyTableWithVirtuoso = memo(function TherapyTableWithVirtuoso({
  flatRows,
  scrollableRef,
  patientProtocols,
  readOnly = false,
}: {
  flatRows: FlatRow[]
  scrollableRef: HTMLElement
  patientProtocols: PatientProtocol[]
  readOnly?: boolean
}) {
  const {t} = useTranslate('common')
  const {selectedTherapyIds, toggleTherapySelection, toggleTherapyGroup, pruneTherapySelection} =
    usePatientTherapyContext()
  const virtuosoRef = useRef<TableVirtuosoHandle | null>(null)
  const visibleRangeRef = useRef({startIndex: 0, endIndex: 0})
  const [isCurrentVisible, setIsCurrentVisible] = useState(false)
  const confirmDelete = useConfirmDeletePatientPrescription()
  // duplicating a prescription creates a new one, so it needs the prescription-edit permission
  const canEditPrescriptions = useCan('prescriptions.edit')
  const currentRowIndex = flatRows.findIndex(r => r.type === 'date-header' && r.isCurrent)
  const selectableIds = useMemo(() => getSelectableRowIds(flatRows), [flatRows])
  const selectAllState = getGroupSelectionState(selectedTherapyIds, selectableIds)

  // filters remove rows; drop their ids so Print never includes a hidden prescription
  useEffect(() => {
    pruneTherapySelection(selectableIds)
  }, [selectableIds, pruneTherapySelection])

  const scrollToCurrent = useCallback(() => {
    if (currentRowIndex >= 0 && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: currentRowIndex,
        align: 'start',
        behavior: 'smooth',
      })
    }
  }, [currentRowIndex])

  const onClickDelete = useCallback(
    async (id: string) => {
      await confirmDelete({id: id})
    },
    [confirmDelete]
  )

  const onRowClick = useCallback(
    async (event: MouseEvent, therapy: PatientTherapy) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        toggleTherapySelection(therapy.id, selectableIds, event.shiftKey)
        return
      }

      if (readOnly) {
        await openPatientTherapySequenceDrawer(therapy)
        return
      }

      await openAddParameterDialog({
        patientProtocols: patientProtocols,
        patientId: therapy.patientId,
        existing: therapy,
      })
    },
    [patientProtocols, readOnly, selectableIds, toggleTherapySelection]
  )

  const onClickDuplicate = useCallback(
    async (therapy: PatientTherapy) => {
      if (readOnly) return
      await openAddParameterDialog({
        patientProtocols: patientProtocols,
        patientId: therapy.patientId,
        existing: therapy,
        duplicate: true,
      })
    },
    [patientProtocols, readOnly]
  )

  const handleRangeChanged = useCallback(
    ({startIndex, endIndex}: {startIndex: number; endIndex: number}) => {
      visibleRangeRef.current = {startIndex, endIndex}
      const visible = currentRowIndex >= startIndex && currentRowIndex <= endIndex
      setIsCurrentVisible(prev => (prev === visible ? prev : visible))
    },
    [currentRowIndex]
  )

  const fixedHeader = useCallback(
    () => (
      <Table.Row>
        <Table.ColumnHeader>
          <Checkbox.Root
            size="md"
            aria-label={t('select_all')}
            checked={selectAllState}
            onCheckedChange={() => toggleTherapyGroup(selectableIds)}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
          </Checkbox.Root>
        </Table.ColumnHeader>
        <Table.ColumnHeader></Table.ColumnHeader>
        <Table.ColumnHeader>{t('therapy_sequence.time')}</Table.ColumnHeader>
        <Table.ColumnHeader>{t('protocol')}</Table.ColumnHeader>
        <Table.ColumnHeader>{t('therapy_sequence.table.medication')}</Table.ColumnHeader>
        <Table.ColumnHeader>{t('dose')}</Table.ColumnHeader>
        <Table.ColumnHeader>{t('solution')}</Table.ColumnHeader>
        <Table.ColumnHeader>{t('therapy_sequence.table.administration')}</Table.ColumnHeader>
        <Table.ColumnHeader>{t('prescription')}</Table.ColumnHeader>
        <Table.ColumnHeader>{t('status')}</Table.ColumnHeader>
        <Table.ColumnHeader></Table.ColumnHeader>
      </Table.Row>
    ),
    [t, selectAllState, selectableIds, toggleTherapyGroup]
  )

  const itemContent = useCallback(
    (_index: number, item: FlatRow) => {
      const therapyId = getRowTherapyId(item)
      const isSelected = therapyId != null && selectedTherapyIds.has(therapyId)
      const dayRowIds = item.type === 'date-header' ? getDayRowIds(flatRows, item.dateKey) : []
      const daySelectionState = getGroupSelectionState(selectedTherapyIds, dayRowIds)

      return (
        <TherapyRowVirtuoso
          row={item}
          isSelected={isSelected}
          daySelectionState={daySelectionState}
          onToggleSelection={shift => {
            if (therapyId != null) toggleTherapySelection(therapyId, selectableIds, shift)
          }}
          onToggleDay={() => toggleTherapyGroup(dayRowIds)}
          onClickDelete={canEditPrescriptions ? onClickDelete : undefined}
          onRowClick={onRowClick}
          onViewDetails={openPatientTherapySequenceDrawer}
          onClickDuplicate={canEditPrescriptions ? onClickDuplicate : undefined}
          patientProtocols={patientProtocols}
          onDelete={canEditPrescriptions ? confirmDelete : undefined}
          t={t}
        />
      )
    },
    [
      onClickDelete,
      onRowClick,
      onClickDuplicate,
      patientProtocols,
      confirmDelete,
      canEditPrescriptions,
      t,
      flatRows,
      selectedTherapyIds,
      selectableIds,
      toggleTherapySelection,
      toggleTherapyGroup,
    ]
  )

  return (
    <>
      {!isCurrentVisible && currentRowIndex >= 0 && (
        <Button
          position="fixed"
          onClick={scrollToCurrent}
          bottom="50px"
          right="50px"
          backgroundColor="cyan.subtle"
          borderColor="cyan.muted"
          borderRadius={'100px'}
          zIndex={1000}
        >
          <Box display="flex" flexDirection="row" gap="2" color={'cyan.fg'}>
            <Text color={'cyan.fg'}>{t('next_visit')}</Text>
            {currentRowIndex < visibleRangeRef.current.startIndex ? <LuMoveUp /> : <LuMoveDown />}
          </Box>
        </Button>
      )}
      {flatRows.length > 0 ? (
        <TableVirtuoso
          ref={virtuosoRef}
          style={{width: '100%'}}
          totalCount={flatRows.length}
          data={flatRows}
          components={virtuosoTableComponents}
          increaseViewportBy={{top: 0, bottom: 100}}
          rangeChanged={handleRangeChanged}
          customScrollParent={scrollableRef}
          fixedHeaderContent={fixedHeader}
          itemContent={itemContent}
        />
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh" flexDirection={'column'} gap="4">
          <LuTextSearch size="32" color="gray" />
          <Text fontSize="sm" color="fg.muted">
            {t('no_associated_prescription')}
          </Text>
        </Box>
      )}
    </>
  )
})
